import React, { useState } from 'react';
import { Calendar, Users, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { format } from 'date-fns';
import { hr } from 'date-fns/locale';
import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_ljo9qcs';
const EMAILJS_TEMPLATE_ID = 'template_55y9smc';
const EMAILJS_PUBLIC_KEY = '5RRMCwoC_-zpQCXcs';

const Reservation = () => {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut || !guests || !name || !email || !agreed) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Prepare template parameters
      const templateParams = {
        from_name: name,
        from_email: email,
        check_in: format(checkIn, 'dd.MM.yyyy', { locale: hr }),
        check_out: format(checkOut, 'dd.MM.yyyy', { locale: hr }),
        guests: guests,
        message: message || 'Nema dodatne poruke',
      };

      // Send email via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS Error:', err);
      setError('Došlo je do greške pri slanju. Molimo pokušajte ponovo ili nas kontaktirajte direktno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="rezervacija" className="py-20 bg-stone-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Upit je poslan!</h3>
            <p className="text-gray-600 mb-6">
              Hvala vam na interesu. Javit ćemo vam se u najkraćem mogućem roku s ponudom.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setCheckIn(null);
                setCheckOut(null);
                setGuests('');
                setName('');
                setEmail('');
                setMessage('');
                setAgreed(false);
              }}
              className="bg-sage-600 hover:bg-sage-700"
            >
              Pošaljite novi upit
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="rezervacija" className="py-20 bg-stone-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sage-600 text-sm font-medium tracking-wider mb-2">Rezervacija</p>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-4">
            Pošaljite upit
          </h2>
          <p className="text-gray-600">
            Ispunite formu i javit ćemo vam se s ponudom u najkraćem roku.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Check-in Date */}
            <div className="space-y-2">
              <Label>Datum dolaska *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${
                      !checkIn && 'text-muted-foreground'
                    }`}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {checkIn ? format(checkIn, 'PPP', { locale: hr }) : 'Odaberite datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkIn}
                    onSelect={setCheckIn}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Check-out Date */}
            <div className="space-y-2">
              <Label>Datum odlaska *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`w-full justify-start text-left font-normal ${
                      !checkOut && 'text-muted-foreground'
                    }`}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {checkOut ? format(checkOut, 'PPP', { locale: hr }) : 'Odaberite datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={checkOut}
                    onSelect={setCheckOut}
                    disabled={(date) => date < (checkIn || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Number of Guests */}
          <div className="space-y-2 mb-6">
            <Label>Broj gostiju *</Label>
            <Select value={guests} onValueChange={setGuests}>
              <SelectTrigger>
                <SelectValue placeholder="Odaberite broj gostiju" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 osoba</SelectItem>
                <SelectItem value="2">2 osobe</SelectItem>
                <SelectItem value="2+1">2 osobe + dijete</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="space-y-2 mb-6">
            <Label>Ime i prezime *</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vaše ime i prezime"
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2 mb-6">
            <Label>Email adresa *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vasa@email.com"
              required
            />
          </div>

          {/* Message */}
          <div className="space-y-2 mb-6">
            <Label>Poruka (opcionalno)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Dodatne napomene ili pitanja..."
              rows={4}
            />
          </div>

          {/* Agreement */}
          <div className="flex items-start gap-3 mb-8">
            <Checkbox
              id="agreement"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1"
            />
            <Label htmlFor="agreement" className="text-sm text-gray-600 cursor-pointer">
              Slažem se s obradom osobnih podataka u svrhu obrade upita *
            </Label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={!checkIn || !checkOut || !guests || !name || !email || !agreed || isSubmitting}
            className="w-full bg-sage-600 hover:bg-sage-700 text-white py-6 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Šaljem...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Send size={20} />
                Pošalji upit
              </span>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default Reservation;